import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useAudienceSegments } from "@/hooks/useAudienceSegments";
import { SegmentEditView } from "@/components/admin/audience-segments/SegmentEditView";
import { Pencil, Trash2, Plus, Users, RefreshCw, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const AudienceSegmentsContent: React.FC = () => {
  const { toast } = useToast();
  const { segments, isLoading, updateSegment, deleteSegment, refreshSegmentCount, refetch } = useAudienceSegments();
  const [editingSegment, setEditingSegment] = useState<any>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  const handleEdit = (segment: any) => {
    setEditingSegment(segment);
  };

  const handleCreate = () => {
    setIsCreating(true);
  };

  const handleBackFromEdit = () => {
    setEditingSegment(null);
    setIsCreating(false);
    refetch();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    
    try {
      await deleteSegment(deleteId);
      toast({
        title: "Segment deleted",
        description: "The audience segment has been deleted successfully.",
      });
      setDeleteId(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete segment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (segment: any) => {
    try {
      await updateSegment(segment.id, { is_active: !segment.is_active });
      toast({
        title: "Status updated",
        description: `Segment ${segment.is_active ? 'deactivated' : 'activated'} successfully.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update segment status.",
        variant: "destructive",
      });
    }
  };

  const handleRefreshCount = async (segment: any) => {
    setRefreshingId(segment.id);
    try {
      const count = await refreshSegmentCount(segment);
      toast({
        title: "Count updated",
        description: `Segment now has ${count.toLocaleString()} estimated recipients.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh recipient count.",
        variant: "destructive",
      });
    } finally {
      setRefreshingId(null);
    }
  };

  const formatFilters = (segment: any) => {
    const filters = [];
    if (segment.explicit_profile_ids?.length) {
      filters.push(`${segment.explicit_profile_ids.length} explicit profile(s)`);
    }
    if (segment.sports?.length) filters.push(`${segment.sports.length} sport(s)`);
    if (segment.gender?.length) filters.push(`${segment.gender.join(', ')}`);
    if (segment.grad_year_min || segment.grad_year_max) {
      filters.push(`Grad: ${segment.grad_year_min || '?'}-${segment.grad_year_max || '?'}`);
    }
    if (segment.states?.length) filters.push(`${segment.states.length} state(s)`);
    if (segment.membership_types?.length) filters.push(`${segment.membership_types.length} membership(s)`);
    return filters.join(' • ');
  };

  // Show inline edit view when editing or creating
  if (editingSegment || isCreating) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={handleBackFromEdit}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Segments
          </Button>
        </div>
        <SegmentEditView
          segment={editingSegment}
          onBack={handleBackFromEdit}
          onSuccess={handleBackFromEdit}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Create Segment
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Segments</CardTitle>
          <CardDescription>
            Manage your email audience segments and view estimated recipients
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : segments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No segments yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first audience segment to start sending targeted emails
              </p>
              <Button onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Create First Segment
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Segment Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Filters</TableHead>
                    <TableHead className="text-right">Est. Recipients</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {segments.map((segment) => (
                    <TableRow key={segment.id}>
                      <TableCell className="font-medium">{segment.segment_name}</TableCell>
                      <TableCell className="max-w-xs truncate">
                        {segment.description || '—'}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatFilters(segment) || 'No filters'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Badge variant="secondary">
                            {segment.estimated_recipients?.toLocaleString() || '—'}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRefreshCount(segment)}
                            disabled={refreshingId === segment.id}
                          >
                            <RefreshCw className={`h-4 w-4 ${refreshingId === segment.id ? 'animate-spin' : ''}`} />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={segment.is_active}
                          onCheckedChange={() => handleToggleActive(segment)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(segment)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteId(segment.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Segment?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the audience segment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AudienceSegmentsContent;
